import { useState, useEffect, memo } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { yupResolver } from '@hookform/resolvers/yup';
import client from '../../../client';
import * as yup from 'yup';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import FuseMessage from '@fuse/core/FuseMessage';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import { useTranslation } from 'react-i18next';

const schema = yup.object().shape({
  product_name: yup.string().required('Product name is required'),
  description: yup.string().nullable().max(255, 'Description must be 255 characters or less'),
});

function ModalCreateContentType({ isOpen, setIsOpen, onCreated }) {
  const { t } = useTranslation('navigation');
  const dispatch = useDispatch();
  const [open, setOpen] = useState(isOpen);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const handleClose = () => {
    setOpen(false);
    setIsOpen(false);
  };

  // Get compacctnum from localStorage
  let compacctnum = '';
  try {
    const lSData = JSON.parse(localStorage.getItem('persist:root'));
    const userData = JSON.parse(lSData.loadz_auth).AuthReducer.user;
    compacctnum = userData.company.compacctnum;
  } catch (e) {
    compacctnum = '';
  }

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      compacctnum: compacctnum,
      product_name: '',
      description: '',
    },
    resolver: yupResolver(schema),
  });

  const clearForm = {
    compacctnum: compacctnum,
    product_name: '',
    description: '',
  };

  const _showMessage = (msg, color) => {
    dispatch(
      showMessage({
        message: msg,
        autoHideDuration: 6000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: color,
      })
    );
  };

  const { formState, getValues, reset, control } = methods;
  const { isValid, errors } = formState;

  const handleCreateContentType = async () => {
    try {
      const data = JSON.parse(JSON.stringify(getValues()));
      data.compacctnum = compacctnum;
      
      const response = await client.post('/fuel-inventory/create-content-type/', data);
      
      if (response.data.message) {
        _showMessage(response.data.message, 'error');
        return;
      }
      
      if (onCreated) onCreated(response.data);
      _showMessage(t('CONTENT_TYPE_CREATED_SUCCESSFULLY'), 'success');
      setOpen(false);
      reset(clearForm);
      setIsOpen(false);
    } catch (error) {
      console.error('Error creating content type:', error);
      _showMessage(t('SOMETHING_WENT_WRONG'), 'error');
    }
  };

  return (
    <div>
      <FuseMessage />
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('CREATE_NEW_CONTENT_TYPE')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('COMPLETE_FORM_CREATE_CONTENT_TYPE')}
          </DialogContentText>
          <br />
          <FormProvider {...methods}>
            <div className="flex flex-col gap-4">
              <label>
                {t('PRODUCT_NAME_REQUIRED')}
              </label>
              <Controller
                name="product_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    error={!!errors.product_name}
                    helperText={errors?.product_name?.message}
                    variant="outlined"
                    fullWidth
                    inputProps={{ style: { padding: '5px' } }}
                    placeholder="e.g., Diesel, Gasoline, Oil"
                  />
                )}
              />
              
              <label>
                {t('DESCRIPTION')}
              </label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.description}
                    helperText={errors?.description?.message}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    inputProps={{ style: { padding: '5px' } }}
                    placeholder="Optional description of the content type"
                  />
                )}
              />
            </div>
          </FormProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('CANCEL')}</Button>
          <Button 
            onClick={handleCreateContentType} 
            variant="contained"
            disabled={!isValid}
          >
            {t('CREATE')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default memo(ModalCreateContentType);
